import type { InvoiceOCRResult } from './ocr-cloud';

/**
 * 解析中文大写金额为数字
 * 例如："捌拾陆圆伍角整" -> 86.50
 */
function parseCapitalAmount(capitalStr: string): number {
  if (!capitalStr || typeof capitalStr !== 'string') return 0;

  // 移除空格和常见的前缀符号
  capitalStr = capitalStr.replace(/\s+/g, '').replace(/^[⊗○●◎]/g, '').trim();

  // 中文数字映射
  const digitMap: Record<string, number> = {
    '零': 0, '壹': 1, '贰': 2, '叁': 3, '肆': 4,
    '伍': 5, '陆': 6, '柒': 7, '捌': 8, '玖': 9,
    // 也支持简体
    '〇': 0, '一': 1, '二': 2, '三': 3, '四': 4,
    '五': 5, '六': 6, '七': 7, '八': 8, '九': 9
  };

  const unitMap: Record<string, number> = {
    '拾': 10, '佰': 100, '仟': 1000, '万': 10000, '萬': 10000,
    '十': 10, '百': 100, '千': 1000,
    '亿': 100000000, '億': 100000000
  };

  let result = 0;
  let currentNum = 0;
  let temp = 0;
  let decimalPart = 0;

  // 分离整数和小数部分（以元/圆为界）
  const parts = capitalStr.split(/[元圆圓]/);
  const integerPart = parts[0] || '';
  const decimalStr = parts[1] || '';

  // 解析整数部分
  for (let i = 0; i < integerPart.length; i++) {
    const char = integerPart[i];

    if (digitMap.hasOwnProperty(char)) {
      currentNum = digitMap[char];
    } else if (unitMap.hasOwnProperty(char)) {
      const unit = unitMap[char];
      if (unit >= 10000) {
        // 万、亿
        temp += currentNum;
        result += temp * unit;
        temp = 0;
        currentNum = 0;
      } else {
        // 拾、佰、仟
        if (currentNum === 0 && char === '拾') currentNum = 1; // 处理"拾"开头
        temp += currentNum * unit;
        currentNum = 0;
      }
    }
  }
  result += temp + currentNum;

  // 解析小数部分（角、分）
  for (let i = 0; i < decimalStr.length; i++) {
    const char = decimalStr[i];
    if (digitMap.hasOwnProperty(char)) {
      const nextChar = decimalStr[i + 1];
      if (nextChar === '角') {
        decimalPart += digitMap[char] * 0.1;
      } else if (nextChar === '分') {
        decimalPart += digitMap[char] * 0.01;
      }
    }
  }

  const total = result + decimalPart;
  return Math.round(total * 100) / 100;
}

/**
 * Parse invoice information from extracted text
 * Supports both Simplified and Traditional Chinese
 */
export function parseInvoiceFromText(text: string): InvoiceOCRResult {
  console.log('[发票解析] 开始解析发票文本...');
  console.log('[发票解析] 原始文本:', text.substring(0, 200));

  // 移除字符之间的多余空格（某些PDF提取出来每个字符之间都有空格）
  // 例如 "¥ 1 5 7 9 . 4 9" -> "¥1579.49"
  // 但保留正常的词语间隔
  const normalizedText = text
    // 先处理金额：¥ 1 5 7 9 . 4 9 -> ¥1579.49
    .replace(/[¥￥]\s*(\d(?:\s*\d)*(?:\s*\.\s*\d(?:\s*\d)?)?)/g, (match) => {
      return match.replace(/\s+/g, '');
    })
    // 处理日期和数字中的OCR空格伪影：2 0 2 5 年 -> 2025年
    // 只合并连续单字符数字（如 "2 5 4 3 7"），不合并独立数字组（如 "319838288 2025"）
    .replace(/(?<!\d)(\d(?:\s+\d){2,})(?!\d)/g, (match) => match.replace(/\s+/g, ''))
    // 处理中文关键词之间的空格
    .replace(/发\s*票\s*号\s*码/g, '发票号码')
    .replace(/开\s*票\s*日\s*期/g, '开票日期')
    .replace(/价\s*税\s*合\s*计/g, '价税合计')
    .replace(/小\s*写/g, '小写')
    .replace(/大\s*写/g, '大写')
    .replace(/合\s*计/g, '合计')
    .replace(/金\s*额/g, '金额');

  console.log('[发票解析] 规范化后:', normalizedText.substring(0, 200));
  text = normalizedText;

  // 提取发票号码 (支持繁简体)
  const invoiceNumberPatterns = [
    /发票号码[:\s：]*(\d{15,20})/,
    /發票號碼[:\s：]*(\d{15,20})/,
    /号码[:\s：]*(\d{15,20})/,
    /號碼[:\s：]*(\d{15,20})/,
    /Invoice No[.:\s]*(\d{15,20})/i,
    /No[.:\s]*(\d{15,20})/,
    /(?<!\d)(\d{20})(?!\d)/  // 恰好独立的20位数字（中国电子发票标准长度）
  ];

  // 如果没找到发票号，可能是因为数字之间有空格，再尝试一次
  // 先把文本中连续的"数字+空格"模式提取出来
  if (!invoiceNumberPatterns.some(p => p.test(text))) {
    // 找 "发票号码" 后面的内容，提取所有数字
    const afterInvoiceNum = text.match(/发票号码[:\s：]*([\d\s]{20,50})/);
    if (afterInvoiceNum && afterInvoiceNum[1]) {
      const cleanedNum = afterInvoiceNum[1].replace(/\s+/g, '');
      if (cleanedNum.length >= 15) {
        text = text.replace(afterInvoiceNum[0], `发票号码：${cleanedNum}`);
        console.log('[发票解析] 修复发票号码空格:', cleanedNum);
      }
    }
  }

  let invoiceNumber = '';
  for (const pattern of invoiceNumberPatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      invoiceNumber = match[1];
      console.log('[发票解析] 发票号码:', invoiceNumber);
      break;
    }
  }

  let amount = 0;
  let taxAmount = 0;

  // === 第一步：提取大写金额 ===
  // 大写金额格式：捌拾陆圆伍角整、壹仟贰佰叁拾肆圆伍角陆分
  let capitalAmount = 0;
  const CAPITAL_CHARS = '零壹贰叁肆伍陆柒捌玖拾佰仟万亿〇一二三四五六七八九十百千萬億元圆圓角分整正';
  const capitalPatterns = [
    // 价税合计（大写）后面的大写金额
    new RegExp(`价税合计[\\s\\S]{0,20}[（\\(]\\s*大写\\s*[）\\)]\\s*[⊗○●◎]?\\s*([${CAPITAL_CHARS}\\s]+)`),
    // 大写金额直接匹配（⊗ 开头）
    new RegExp(`[⊗○●◎]\\s*([${CAPITAL_CHARS}\\s]+)`),
    // 更宽松的匹配
    new RegExp(`([零壹贰叁肆伍陆柒捌玖拾佰仟万〇一二三四五六七八九十百千\\s]+[元圆圓][零壹贰叁肆伍陆柒捌玖〇一二三四五六七八九角分整正\\s]*)`)
  ];

  for (const pattern of capitalPatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      // 去掉内嵌空格后再解析（PDF文本提取有时在汉字间插入空格）
      const cleanedMatch = match[1].replace(/\s+/g, '');
      const parsed = parseCapitalAmount(cleanedMatch);
      if (parsed > 0 && parsed < 1000000) {
        capitalAmount = parsed;
        console.log('[发票解析] 大写金额:', match[1].trim(), '->', capitalAmount);
        break;
      }
    }
  }

  // === 第二步：提取小写金额 ===
  const allAmounts: { amount: number; context: string; priority: number }[] = [];

  // 找所有 ¥ 金额
  const amountRegex = /([^\d¥￥]{0,30})[￥¥]\s*(\d+\.?\d*)/g;
  let amountMatch;
  while ((amountMatch = amountRegex.exec(text)) !== null) {
    const context = amountMatch[1] || '';
    const foundAmount = parseFloat(amountMatch[2]);
    if (foundAmount > 0 && foundAmount < 1000000) {
      // 根据上下文确定优先级（数字越小优先级越高）
      let priority = 50;  // 默认优先级

      // 最高优先级：明确的"小写"标记
      if (/[（\(]\s*小写\s*[）\)]/.test(context) || /小写\s*$/.test(context)) {
        priority = 1;
      }
      // 高优先级：大写金额后面紧跟的（如 "圆整 ¥86.50"）
      else if (/[圆圓元][整正]\s*$/.test(context)) {
        priority = 2;
      }
      // 中优先级：价税合计行的金额
      else if (/价税合计/.test(context)) {
        priority = 10;
      }
      // 低优先级：合计行的金额
      else if (/合计/.test(context)) {
        priority = 20;
      }
      // 较低优先级：金额标签
      else if (/金额/.test(context)) {
        priority = 30;
      }
      // 税额应该被排除
      else if (/税额|稅額/.test(context)) {
        priority = 100;
      }

      allAmounts.push({
        amount: foundAmount,
        context: amountMatch[0].substring(0, 60),
        priority
      });
    }
  }

  console.log('[发票解析] 所有小写金额:', allAmounts);

  // === 第三步：交叉验证大写和小写金额 ===
  if (capitalAmount > 0 && allAmounts.length > 0) {
    // 查找与大写金额匹配的小写金额（允许0.01的误差）
    const matchingAmount = allAmounts.find(a => Math.abs(a.amount - capitalAmount) < 0.02);
    if (matchingAmount) {
      amount = matchingAmount.amount;
      console.log('[发票解析] ✅ 大写小写交叉验证成功:', capitalAmount, '=', amount);
    } else {
      // 大写金额和小写金额不匹配
      // 如果大写金额是整数（可能是PDF只提取到了元，角分丢失），
      // 检查是否有小写金额整数部分与之相同（如大写=50，小写=50.81）
      const explicitSmall = allAmounts.find(a => a.priority === 1);
      const partialMatch = !explicitSmall && Number.isInteger(capitalAmount)
        ? allAmounts.find(a => Math.floor(a.amount) === capitalAmount && a.amount > capitalAmount)
        : undefined;

      if (explicitSmall) {
        amount = explicitSmall.amount;
        console.log('[发票解析] ⚠️ 大写小写不匹配，使用明确标记的小写金额:', amount);
      } else if (partialMatch) {
        amount = partialMatch.amount;
        console.log('[发票解析] ⚠️ 大写金额解析不完整（缺角分），使用小写金额:', amount);
      } else {
        amount = capitalAmount;
        console.log('[发票解析] ⚠️ 大写小写不匹配，使用大写金额:', capitalAmount);
      }
    }
  } else if (capitalAmount > 0) {
    // 只有大写金额
    amount = capitalAmount;
    console.log('[发票解析] 只找到大写金额:', amount);
  } else if (allAmounts.length > 0) {
    // 只有小写金额，按优先级选择
    const bestMatch = allAmounts.reduce((best, curr) => {
      if (curr.priority < best.priority) return curr;
      if (curr.priority === best.priority && curr.amount > best.amount) return curr;
      return best;
    });
    amount = bestMatch.amount;
    console.log('[发票解析] 选择最佳小写匹配:', bestMatch.context, '-> 金额:', amount, '(优先级:', bestMatch.priority, ')');
  } else {
    console.log('[发票解析] 未找到金额');
  }

  // 提取日期 (支持繁简体) - 支持"2025年 08月 01日"这种格式
  const datePatterns = [
    /开票日期[:\s：]*(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*[日号]/,  // 2025年 08月 01日
    /開票日期[:\s：]*(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*[日号]/,
    /开票日期[:\s：]*(\d{4})[年\-\/](\d{1,2})[月\-\/](\d{1,2})/,
    /開票日期[:\s：]*(\d{4})[年\-\/](\d{1,2})[月\-\/](\d{1,2})/,
    /日期[:\s：]*(\d{4})[年\-\/](\d{1,2})[月\-\/](\d{1,2})/,
    /Date[:\s]*(\d{4})[年\-\/](\d{1,2})[月\-\/](\d{1,2})/i,
    /(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*[日号]/,  // 通用格式
    /(\d{4})[年\-\/](\d{1,2})[月\-\/](\d{1,2})[日号]/,
    /(\d{4})-(\d{2})-(\d{2})/
  ];

  let date = new Date().toISOString().split('T')[0];
  for (const pattern of datePatterns) {
    const match = text.match(pattern);
    if (match && match[1] && match[2] && match[3]) {
      const year = match[1];
      const month = match[2].trim().padStart(2, '0');
      const day = match[3].trim().padStart(2, '0');
      date = `${year}-${month}-${day}`;
      console.log('[发票解析] 日期:', date);
      break;
    }
  }

  // 提取税额 (支持繁简体) - 如果前面三连金额没有提取到税额
  if (taxAmount === 0) {
    const taxPatterns = [
      /[税稅][额額][:\s：]*[￥¥]?\s*(\d+\.?\d*)/,
      /合[计計税稅][额額][:\s：]*[￥¥]?\s*(\d+\.?\d*)/,
      /Tax[:\s]*[￥¥$]?\s*(\d+\.?\d*)/i
    ];

    for (const pattern of taxPatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        taxAmount = parseFloat(match[1]);
        console.log('[发票解析] 税额:', taxAmount);
        break;
      }
    }
  }

  // 提取购买方名称 (支持繁简体)
  const buyerPatterns = [
    /购买方[名称]*[:\s：]*([^\n\r]{2,50})/,
    /購買方[名稱]*[:\s：]*([^\n\r]{2,50})/,
    /[买買]方[:\s：]*([^\n\r]{2,50})/,
    /Buyer[:\s]*([^\n\r]{2,50})/i
  ];

  let buyerName = '';
  for (const pattern of buyerPatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      buyerName = match[1].trim();
      console.log('[发票解析] 购买方:', buyerName);
      break;
    }
  }

  // 提取销售方名称 (支持繁简体)
  const sellerPatterns = [
    /销售方[名称]*[:\s：]*([^\n\r]{2,50})/,
    /銷售方[名稱]*[:\s：]*([^\n\r]{2,50})/,
    /[销銷]方[:\s：]*([^\n\r]{2,50})/,
    /Seller[:\s]*([^\n\r]{2,50})/i
  ];

  let sellerName = '';
  for (const pattern of sellerPatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      sellerName = match[1].trim();
      console.log('[发票解析] 销售方:', sellerName);
      break;
    }
  }

  // 识别发票类型 (支持繁简体)
  let invoiceType = '电子发票';
  if (text.includes('增值税') || text.includes('增值稅')) {
    invoiceType = '增值税发票';
  } else if (text.includes('普通发票') || text.includes('普通發票')) {
    invoiceType = '普通发票';
  } else if (text.includes('电子发票') || text.includes('電子發票')) {
    invoiceType = '电子发票';
  }

  const result: InvoiceOCRResult = {
    invoiceNumber,
    amount,
    date,
    invoiceType,
    taxAmount,
    buyerName,
    sellerName,
    confidence: 0.98  // 文本提取比OCR更准确
  };

  console.log('[发票解析] 解析完成:', JSON.stringify(result, null, 2));
  return result;
}
